import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, LocPortal, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Candidate|History|Fulfillment Address Confirmation';
const sharedProps = {
	htmlAllowed: false,
	shared: false,
	portal: 'SP' as LocPortal,
	feature: HubFeature.CredentialFulfillment,
};

export type AddressConfirmationLoc =
	| 'SPAddressConfirmationUpdateButton'
	| 'SPAddressConfirmationConfirmButton'
	| 'SPAddressConfirmationConfirmedButton'
	| 'SPAddressConfirmationCancelButton'
	| 'SPAddressConfirmationMailingAddressTitle'
	| 'SPAddressConfirmationLocalizedAddressTitle'
	| 'SPAddressConfirmationCountryLabel'
	| 'SPAddressConfirmationAddressLabel'
	| 'SPAddressConfirmationAddress2Label'
	| 'SPAddressConfirmationAddress3Label'
	| 'SPAddressConfirmationCityLabel'
	| 'SPAddressConfirmationStateLabel'
	| 'SPAddressConfirmationPostalCodeLabel'
	| 'SPAddressConfirmationPhoneLabel'
	| 'SPAddressConfirmationBusinessCheckbox'
	| 'SPAddressConfirmationSubmitButton'
	| 'SPAddressConfirmationInvalidCountryError'
	| 'SPAddressConfirmationInvalidStateError'
	| 'SPAddressConfirmationPostalCodeUSAError'
	| 'SPAddressConfirmationPostalCodeCanadaError'
	| 'SPAddressConfirmationPhoneFormatError'
	| 'SPAddressConfirmationPhoneZerosError'
	| 'SPAddressConfirmationRequiredFieldsMissing'
	| 'SPAddressConfirmationAddressRequiredError'
	| 'SPAddressConfirmationCityRequiredError'
	| 'SPAddressConfirmationStateRequiredError'
	| 'SPAddressConfirmationStateRequiredVueHint'
	| 'SPAddressConfirmationStateInvalidError'
	| 'SPAddressConfirmationPostalCodeRequiredError'
	| 'SPAddressConfirmationPhoneRequiredError'
	| 'SPAddressConfirmationPhoneUsaCanadaError'
	| 'SPAddressConfirmationCountryRequiredError'
	| 'SPAddressConfirmationBusinessCheckboxLabel'
	| 'SPAddressConfirmationLocalizedAddressTitle';

export const spAddressConfirmationLocs: LocDefs<AddressConfirmationLoc> = {
	SPAddressConfirmationUpdateButton: {
		defaultValue: 'Update and Confirm Address',
		displayType: 'Button',
		description: 'Button text to update the address',
		displayPath: `${dpRoot}|Update Button`,
		...sharedProps,
	},
	SPAddressConfirmationConfirmButton: {
		defaultValue: 'Confirm address',
		displayType: 'Button',
		description: 'Button text to confirm the address',
		displayPath: `${dpRoot}|Confirm Button`,
		...sharedProps,
	},
	SPAddressConfirmationConfirmedButton: {
		defaultValue: 'Confirmed',
		displayType: 'Button',
		description: 'Button text when the address is confirmed',
		displayPath: `${dpRoot}|Confirmed Button`,
		...sharedProps,
	},
	SPAddressConfirmationCancelButton: {
		defaultValue: 'Cancel',
		displayType: 'Button',
		description: 'Button text to cancel the address update',
		displayPath: `${dpRoot}|Cancel Button`,
		...sharedProps,
	},
	SPAddressConfirmationMailingAddressTitle: {
		defaultValue: 'Mailing Address',
		displayType: 'Title',
		description: 'Title for the mailing address form section',
		displayPath: `${dpRoot}|Mailing Address Title`,
		...sharedProps,
	},
	SPAddressConfirmationLocalizedAddressTitle: {
		defaultValue: 'Localized Address',
		displayType: 'Title',
		description: 'Title for the localized address form section',
		displayPath: `${dpRoot}|Localized Address Title`,
		...sharedProps,
	},
	SPAddressConfirmationCountryLabel: {
		defaultValue: 'Country',
		displayType: 'Label',
		description: 'Label for the country field',
		displayPath: `${dpRoot}|Country Label`,
		...sharedProps,
	},
	SPAddressConfirmationAddressLabel: {
		defaultValue: 'Address',
		displayType: 'Label',
		description: 'Label for the address field',
		displayPath: `${dpRoot}|Address Label`,
		...sharedProps,
	},
	SPAddressConfirmationAddress2Label: {
		defaultValue: 'Address (cont.)',
		displayType: 'Label',
		description: 'Label for the second address field',
		displayPath: `${dpRoot}|Address 2 Label`,
		...sharedProps,
	},
	SPAddressConfirmationAddress3Label: {
		defaultValue: 'Address (cont.)',
		displayType: 'Label',
		description: 'Label for the third address field',
		displayPath: `${dpRoot}|Address 3 Label`,
		...sharedProps,
	},
	SPAddressConfirmationCityLabel: {
		defaultValue: 'City',
		displayType: 'Label',
		description: 'Label for the city field',
		displayPath: `${dpRoot}|City Label`,
		...sharedProps,
	},
	SPAddressConfirmationStateLabel: {
		defaultValue: 'State/Province',
		displayType: 'Label',
		description: 'Label for the state/province field',
		displayPath: `${dpRoot}|State Label`,
		...sharedProps,
	},
	SPAddressConfirmationPostalCodeLabel: {
		defaultValue: 'Zip/Postal Code',
		displayType: 'Label',
		description: 'Label for the postal code field',
		displayPath: `${dpRoot}|Postal Code Label`,
		...sharedProps,
	},
	SPAddressConfirmationPhoneLabel: {
		defaultValue: 'Phone',
		displayType: 'Label',
		description: 'Label for the phone field',
		displayPath: `${dpRoot}|Phone Label`,
		...sharedProps,
	},
	SPAddressConfirmationPhoneRequiredError: {
		defaultValue: 'Phone is required',
		displayType: 'Error',
		description: 'Error message for required phone field',
		displayPath: `${dpRoot}|Phone Required Error`,
		...sharedProps,
	},
	SPAddressConfirmationCountryRequiredError: {
		defaultValue: 'Country is required',
		displayType: 'Error',
		description: 'Error message for required countryfield',
		displayPath: `${dpRoot}|Country Required Error`,
		...sharedProps,
	},

	SPAddressConfirmationPhoneUsaCanadaError: {
		defaultValue: 'Phone number must have 10 numbers',
		displayType: 'Error',
		description: 'Error message for invalid USA/Canada phone number format',
		displayPath: `${dpRoot}|Phone USA/Canada Error`,
		...sharedProps,
	},
	SPAddressConfirmationPhoneZerosError: {
		defaultValue: 'Phone number must not be all zeros',
		displayType: 'Error',
		description: 'Error message for phone number with all zeros',
		displayPath: `${dpRoot}|Phone Zeros Error`,
		...sharedProps,
	},
	SPAddressConfirmationBusinessCheckbox: {
		defaultValue: 'This is a Business Address',
		displayType: 'Label',
		description: 'Label for the business address checkbox',
		displayPath: `${dpRoot}|Business Address Checkbox`,
		...sharedProps,
	},
	SPAddressConfirmationSubmitButton: {
		defaultValue: 'Update and Confirm Addresses',
		displayType: 'Button',
		description: 'Button text to submit the address form',
		displayPath: `${dpRoot}|Submit Button`,
		...sharedProps,
	},
	SPAddressConfirmationAddressRequiredError: {
		defaultValue: 'Address is required',
		displayType: 'Error',
		description: 'Error message for required fields',
		displayPath: `${dpRoot}|Required Error`,
		...sharedProps,
	},
	SPAddressConfirmationStateRequiredError: {
		defaultValue: 'State/Province is required',
		displayType: 'Error',
		description: 'Error message for required state/province field',
		displayPath: `${dpRoot}|State Required Error`,
		...sharedProps,
	},
	SPAddressConfirmationStateRequiredVueHint: {
		defaultValue: 'State/Province is required. If your state is not listed, please type N/A',
		displayType: 'Error',
		description: 'Error with Vue specific hint for state required.',
		displayPath: `${dpRoot}|State Required Vue Hint`,
		htmlAllowed: false,
		shared: false,
		portal: 'SP' as LocPortal,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},

	SPAddressConfirmationStateInvalidError: {
		defaultValue: 'Invalid state/province',
		displayType: 'Error',
		description: 'Error message for invalid state/province selection',
		displayPath: `${dpRoot}|Invalid State Error`,
		...sharedProps,
	},
	SPAddressConfirmationInvalidCountryError: {
		defaultValue: 'Invalid country',
		displayType: 'Error',
		description: 'Error message for invalid country selection',
		displayPath: `${dpRoot}|Invalid Country Error`,
		...sharedProps,
	},
	SPAddressConfirmationInvalidStateError: {
		defaultValue: 'Invalid state/province',
		displayType: 'Error',
		description: 'Error message for invalid state/province selection',
		displayPath: `${dpRoot}|Invalid State Error`,
		...sharedProps,
	},
	SPAddressConfirmationPostalCodeRequiredError: {
		defaultValue: 'Zip/Postal Code is required',
		displayType: 'Error',
		description: 'Error message for required postal code field',
		displayPath: `${dpRoot}|Postal Code Required Error`,
		...sharedProps,
	},
	SPAddressConfirmationPostalCodeUSAError: {
		defaultValue:
			'Zip/Postal Code must follow one of the two following formats: #####, #####-####',
		displayType: 'Error',
		description: 'Error message for invalid USA postal code format',
		displayPath: `${dpRoot}|USA Postal Code Error`,
		...sharedProps,
	},
	SPAddressConfirmationPostalCodeCanadaError: {
		defaultValue: 'Zip/Postal Code must follow the following format: A#A#A#',
		displayType: 'Error',
		description: 'Error message for invalid Canada postal code format',
		displayPath: `${dpRoot}|Canada Postal Code Error`,
		...sharedProps,
	},
	SPAddressConfirmationPhoneFormatError: {
		defaultValue: 'Phone number must have 10 numbers',
		displayType: 'Error',
		description: 'Error message for invalid phone number format',
		displayPath: `${dpRoot}|Phone Format Error`,
		...sharedProps,
	},
	SPAddressConfirmationRequiredFieldsMissing: {
		defaultValue: 'Required fields in the address are missing. Please update your address.',
		displayType: 'Notification',
		description: 'Error message for phone number with all zeros',
		displayPath: `${dpRoot}|Phone Zeros Error`,
		...sharedProps,
	},
	SPAddressConfirmationCityRequiredError: {
		defaultValue: 'City is required',
		displayType: 'Error',
		description: 'Error message for required city field',
		displayPath: `${dpRoot}|City Required Error`,
		...sharedProps,
	},
	SPAddressConfirmationBusinessCheckboxLabel: {
		defaultValue: 'This is a Business Address',
		displayType: 'Label',
		description: 'Label for the business address checkbox',
		displayPath: `${dpRoot}|Business Address Checkbox Label`,
		...sharedProps,
	},
};

export const LOC_KEYS = MapLocKeys<AddressConfirmationLoc>(spAddressConfirmationLocs);
export type SpAddressConfirmationLocType = LocMap<AddressConfirmationLoc>;
